import type { User } from '../api';
import { EMPLOYEE_ROLE_ORDER, userHasAnyRole } from '../roles';

export type PortalRole = typeof EMPLOYEE_ROLE_ORDER[number];
export type PortalAudience = 'ANY' | 'EMPLOYEE';
export type NavigationGroupId = 'work' | 'directories' | 'service' | 'administration';
export type NavigationIconId =
  | 'home'
  | 'contact-center'
  | 'phone'
  | 'monitoring'
  | 'tickets'
  | 'companies'
  | 'contacts'
  | 'templates'
  | 'planned-works'
  | 'visits'
  | 'mail'
  | 'billing'
  | 'notifications'
  | 'settings'
  | 'admin';

export type PortalScreenId =
  | 'dashboard'
  | 'contactCenter'
  | 'calls'
  | 'tickets'
  | 'ticketView'
  | 'companies'
  | 'contacts'
  | 'templates'
  | 'plannedWorks'
  | 'visits'
  | 'notifications'
  | 'billing'
  | 'notificationSettings'
  | 'monitoring'
  | 'admin';

export type PortalRedirectId = 'tickets-kanban' | 'tickets-new' | 'templates-new' | 'templates-edit' | 'telephony-calls' | 'contact-center-mail' | 'contact-center-mail-compose';

export interface PortalRouteDefinition {
  readonly path: string;
  readonly title: string;
  readonly icon: NavigationIconId;
  readonly audience: PortalAudience;
  readonly roles?: readonly PortalRole[];
  readonly group: NavigationGroupId;
  readonly order: number;
  readonly screen?: PortalScreenId;
  readonly redirect?: PortalRedirectId;
  readonly navigation?: Readonly<{
    label?: string;
    end?: boolean;
  }>;
}

export interface NavigationItem {
  readonly path: string;
  readonly label: string;
  readonly icon: NavigationIconId;
  readonly end: boolean;
}

export interface NavigationGroup {
  readonly id: NavigationGroupId;
  readonly label: string;
  readonly items: readonly NavigationItem[];
}

const ALL_EMPLOYEE_ROLES = Object.freeze<PortalRole[]>([...EMPLOYEE_ROLE_ORDER]);

const ADMIN_ROLES = Object.freeze<PortalRole[]>(['ADMIN']);
const BILLING_ROLES = Object.freeze<PortalRole[]>(['ADMIN', 'MANAGER', 'DISPATCHER', 'EXECUTOR']);

function route(definition: PortalRouteDefinition): PortalRouteDefinition {
  const navigation = definition.navigation ? Object.freeze({ ...definition.navigation }) : undefined;
  return Object.freeze({ ...definition, roles: definition.roles && Object.freeze([...definition.roles]), navigation });
}

export const PORTAL_ROUTES = Object.freeze<readonly PortalRouteDefinition[]>([
  route({ path: '/', title: 'Главная', icon: 'home', audience: 'ANY', group: 'work', order: 10, screen: 'dashboard', navigation: { end: true } }),
  route({ path: '/contact-center', title: 'Контакт-центр', icon: 'contact-center', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'work', order: 20, screen: 'contactCenter', navigation: {} }),
  route({ path: '/calls', title: 'Звонки', icon: 'phone', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'work', order: 30, screen: 'calls', navigation: {} }),
  route({ path: '/telephony', title: 'Звонки', icon: 'phone', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'work', order: 31, redirect: 'telephony-calls' }),
  route({ path: '/tickets', title: 'Заявки', icon: 'tickets', audience: 'ANY', group: 'work', order: 40, screen: 'tickets', navigation: {} }),
  route({ path: '/kanban', title: 'Канбан заявок', icon: 'tickets', audience: 'ANY', group: 'work', order: 41, redirect: 'tickets-kanban' }),
  route({ path: '/tickets/new', title: 'Новая заявка', icon: 'tickets', audience: 'ANY', group: 'work', order: 42, redirect: 'tickets-new' }),
  route({ path: '/tickets/:id', title: 'Заявка', icon: 'tickets', audience: 'ANY', group: 'work', order: 43, screen: 'ticketView' }),
  route({ path: '/companies', title: 'Компании', icon: 'templates', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'directories', order: 10, screen: 'companies', navigation: { label: 'Справочники' } }),
  route({ path: '/contacts', title: 'Контакты', icon: 'contacts', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'directories', order: 20, screen: 'contacts' }),
  route({ path: '/templates', title: 'Шаблоны', icon: 'templates', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'directories', order: 30, screen: 'templates' }),
  route({ path: '/templates/new', title: 'Новый шаблон', icon: 'templates', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'directories', order: 31, redirect: 'templates-new' }),
  route({ path: '/templates/:id/edit', title: 'Редактирование шаблона', icon: 'templates', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'directories', order: 32, redirect: 'templates-edit' }),
  route({ path: '/planned-works', title: 'Плановые работы', icon: 'planned-works', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'service', order: 10, screen: 'plannedWorks', navigation: {} }),
  route({ path: '/visits', title: 'Выезды', icon: 'visits', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'service', order: 15, screen: 'visits', navigation: {} }),
  route({ path: '/mail', title: 'Почта', icon: 'mail', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'service', order: 20, redirect: 'contact-center-mail' }),
  route({ path: '/mail/compose', title: 'Новое письмо', icon: 'mail', audience: 'EMPLOYEE', roles: ALL_EMPLOYEE_ROLES, group: 'service', order: 21, redirect: 'contact-center-mail-compose' }),
  route({ path: '/notifications', title: 'Уведомления', icon: 'notifications', audience: 'ANY', group: 'service', order: 30, screen: 'notifications' }),
  route({ path: '/billing', title: 'Трудозатраты', icon: 'billing', audience: 'EMPLOYEE', roles: BILLING_ROLES, group: 'service', order: 40, screen: 'billing', navigation: {} }),
  route({ path: '/notification-settings', title: 'Настройки уведомлений', icon: 'settings', audience: 'ANY', group: 'service', order: 50, screen: 'notificationSettings' }),
  route({ path: '/monitoring', title: 'Мониторинг', icon: 'monitoring', audience: 'EMPLOYEE', roles: ADMIN_ROLES, group: 'administration', order: 5, screen: 'monitoring', navigation: {} }),
  route({ path: '/admin', title: 'Администрирование', icon: 'admin', audience: 'EMPLOYEE', roles: ADMIN_ROLES, group: 'administration', order: 10, screen: 'admin', navigation: {} }),
]);

const GROUPS: readonly Readonly<{ id: NavigationGroupId; label: string; order: number }>[] = Object.freeze([
  Object.freeze({ id: 'work', label: 'Работа', order: 10 }),
  Object.freeze({ id: 'directories', label: 'Справочники', order: 20 }),
  Object.freeze({ id: 'service', label: 'Сервис', order: 30 }),
  Object.freeze({ id: 'administration', label: 'Управление', order: 40 }),
]);

export function canAccessRoute(
  definition: PortalRouteDefinition,
  user: Pick<User, 'userType' | 'role' | 'roles'>,
): boolean {
  if (definition.audience === 'ANY') return true;
  if (user.userType !== 'EMPLOYEE') return false;
  if (!definition.roles?.length) return true;
  return userHasAnyRole(user, definition.roles);
}

export function getVisibleNavigation(user: Pick<User, 'userType' | 'role' | 'roles'>): NavigationGroup[] {
  return GROUPS.map(group => {
    const items = PORTAL_ROUTES
      .filter(definition => definition.group === group.id && definition.navigation && canAccessRoute(definition, user))
      .sort((left, right) => left.order - right.order)
      .map<NavigationItem>(definition => Object.freeze({
        path: definition.path,
        label: definition.navigation?.label ?? definition.title,
        icon: definition.icon,
        end: definition.navigation?.end ?? false,
      }));

    return Object.freeze({ id: group.id, label: group.label, items: Object.freeze(items) });
  }).filter(group => group.items.length > 0);
}
